// Source-video provenance + "what has landed since the last compile", client
// side. Mirrors packages/functions/shared/video_origin.py — the server is the
// authority (it re-derives all of this in compile_photos), this exists so the
// UI can decide whether to OFFER a rebuild without a round trip.
//
// Keep the two in lock-step: they encode the same rule, in two deployables
// that cannot import each other.

import { MODE_ALL, normalizeSelection, resolveSelection } from './daySelection';

export const ORIGIN_PHOTO_COMPILE = 'photo-compile';
// Multi-video Phase 2: the mp4 the concat worker stream-copied out of a
// project's footage clips. A DERIVED artifact like a compile output (the clips
// are still in storage), but a distinct marker because the two are rebuilt from
// different inputs by different workers.
export const ORIGIN_VIDEO_CONCAT = 'video-concat';
export const ORIGIN_UPLOAD = 'upload';
export const ORIGIN_UNKNOWN = 'unknown';

const KNOWN_ORIGINS = [ORIGIN_PHOTO_COMPILE, ORIGIN_VIDEO_CONCAT, ORIGIN_UPLOAD];

// Video statuses that mean the slot is claimed even before storagePath lands.
const CLAIMED_VIDEO_STATUSES = ['queued', 'uploading', 'analyzing'];

/**
 * Classify a project's source video.
 * @returns {'photo-compile'|'video-concat'|'upload'|'unknown'|null} null when there is no video.
 */
export function resolveVideoOrigin(project) {
  const video = project?.video || {};
  const explicit = video.origin;
  if (KNOWN_ORIGINS.includes(explicit)) return explicit;

  const storagePath = video.storagePath;
  const claimed = !!storagePath || CLAIMED_VIDEO_STATUSES.includes(video.status);
  if (!claimed) return null;

  // Legacy fallback for videos compiled before `origin` was stamped:
  // photoCompile.outputPath is server-written and names the exact object the
  // compile worker produced.
  const compileOutput = project?.photoCompile?.outputPath;
  if (storagePath && compileOutput === storagePath) return ORIGIN_PHOTO_COMPILE;

  // Unknown provenance is treated as uploaded footage — never offer to
  // replace something we cannot prove we generated.
  return ORIGIN_UNKNOWN;
}

/** True when a compile may replace whatever is in the video slot. */
export function canRecompile(project) {
  const origin = resolveVideoOrigin(project);
  return origin === null || origin === ORIGIN_PHOTO_COMPILE;
}

/**
 * True when a COMBINE may claim the project's source-video slot.
 *
 * Enforced in BOTH layers, and neither is redundant. `combine_footage` refuses
 * with FAILED_PRECONDITION / VIDEO_ALREADY_EXISTS (details.origin), exactly as
 * `compile_photos` does — that is the authority, and the only thing that
 * actually stops the destructive outcome: a combine that lands re-points
 * `project.video` at the combined mp4 via the finalize trigger, orphaning the
 * user's original upload in storage (still metered, no longer reachable from
 * the project) with nothing in the UI to recover it.
 *
 * This function is the client's mirror of that rule. Its job is the
 * EXPLANATION, not the protection: it lets the UI render "here is why this
 * project's footage can't be combined" instead of offering a button whose only
 * possible outcome is a bare error toast. Do not delete it because the server
 * checks too — losing it costs the user the reason. Equally, do not weaken the
 * server check because this exists: it must still fail closed, because a stale
 * client snapshot can and does clear a combine the server then refuses (see
 * CombineFootageCta's VIDEO_ALREADY_EXISTS handling).
 *
 * The rule is the callable's, verbatim: `combine_footage` resolves the origin
 * and refuses anything that is not "no video at all" or `video-concat`
 * (timelapse/api/main.py). So this is `canRecompile` with one origin swapped,
 * and it must stay that way — a client rule that says yes where the callable
 * says no produces a button whose only outcome is the blocked card claiming
 * "this project's video changed since this page loaded", which is false.
 *
 * Deliberately NOT the worker's `_replaceable_assets` rule (video_concat.py),
 * which answers a different question — "may we DELETE the old blob after a
 * successful re-combine" — and so carries an extra legacy proof: an unstamped
 * video whose path equals the server-written `videoConcat.outputPath`. That
 * proof does not belong here, because `resolve_video_origin` (the callable's
 * gate) has never consulted `videoConcat.outputPath`. Mirroring the worker
 * instead of the callable is exactly how the two got out of step.
 */
export function canCombineFootage(project) {
  const origin = resolveVideoOrigin(project);
  return origin === null || origin === ORIGIN_VIDEO_CONCAT;
}

/**
 * How much footage has landed since the current video was compiled.
 *
 * Diffs the live coverage rollup against the snapshot the compile worker
 * persisted on photoCompile.compiledFrom (exact: counted off the photos
 * subcollection at encode time). Videos compiled before that field existed
 * fall back to photoCompile.photoCount — the count the callable recorded when
 * the compile was requested — so pre-#381 projects still get the affordance,
 * just without a day breakdown.
 *
 * The diff is scoped to the DAY SELECTION the compile used (#385). A compile
 * limited to five days consumed five days' worth of photos, so subtracting
 * that from the project-wide total would report every unselected photo as
 * newly arrived and permanently nag the user to rebuild.
 *
 * Photos on days the selection does not cover are reported separately as
 * `photosOutsideSelection`. Note the name: this is a COVERAGE figure, not an
 * arrival one — it is simply `totalPhotos - photosInSelection`, so for a user
 * who compiled one week of a three-week shoot it is the other two weeks, and
 * it stays non-zero for as long as the selection stays narrow. Callers must
 * not describe it as "new". A true "arrived since the compile, outside the
 * selection" figure is not derivable here: it would need a coverage baseline
 * captured at compile time (e.g. `compiledFrom.totalPhotos`) to subtract
 * against, and the worker does not persist one.
 *
 * It is still worth surfacing — it is the only signal that there is footage
 * the video does not cover — but the action it implies is "widen the
 * selection", not "rebuild".
 *
 * Returns null when there is nothing to compare against at all, which callers
 * treat as "can't tell, don't nag".
 *
 * Counts are clamped at 0: photos can be removed as well as added, and a
 * negative "new photos" reads as nonsense in the UI.
 */
export function photosSinceCompile(project, coverage) {
  const compileState = project?.photoCompile;
  const snapshot = compileState?.compiledFrom;
  const compiledPhotoCount = typeof snapshot?.photoCount === 'number'
    ? snapshot.photoCount
    : (typeof compileState?.photoCount === 'number' ? compileState.photoCount : null);
  if (compiledPhotoCount === null) return null;

  // Prefer the selection the worker echoed back (exactly what was encoded)
  // over the one on the queued marker, which a later edit may have replaced.
  const selection = normalizeSelection(snapshot?.selection ?? compileState?.selection);
  const coverageDays = coverage?.days || {};
  const totalPhotos = coverage?.totalPhotos || 0;

  const inScope = selection.mode === MODE_ALL
    ? { photoCount: totalPhotos, dayCount: Object.keys(coverageDays).length }
    : resolveSelection(selection, coverageDays);

  // No compiled day count (legacy) → report 0 new days rather than inventing
  // a diff against a baseline we never recorded.
  const compiledDays = typeof snapshot?.dayCount === 'number' ? snapshot.dayCount : inScope.dayCount;

  return {
    newPhotos: Math.max(0, inScope.photoCount - compiledPhotoCount),
    newDays: Math.max(0, inScope.dayCount - compiledDays),
    photosOutsideSelection: Math.max(0, totalPhotos - inScope.photoCount),
    compiledPhotoCount,
    selection,
  };
}
